package com.example.mystybag.login.logic

import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.security.Security
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.login.model.PasswordRequirements
import com.example.mystybag.login.model.StaffChangePasswordUIState
import com.example.mystybag.sharedmodels.project.requestModels.merchant.ChangePasswordStaffRequestModel
import com.example.mystybag.common.resources.SR
import kotlinx.coroutines.launch

class StaffChangePasswordViewModel(
    context: AppContext
) : ChildViewModel<StaffChangePasswordUIState>(context, StaffChangePasswordUIState()) {

    override fun onCreate() {
        navControls.backButtonVisible = true
        navControls.title = sharedString(SR.strings.set_new_password)
    }

    fun onNewPasswordChange(password: String) {
        val requirements = PasswordRequirements(
            hasMinLength = password.length >= 8,
            hasNumber = password.any { it.isDigit() },
            hasLetter = password.any { it.isLetter() }
        )
        updateState {
            it.copy(
                newPassword = password,
                passwordRequirements = requirements,
                newPasswordIsError = false,
                newPasswordErrorMsg = ""
            )
        }
    }

    fun toggleNewPasswordVisibility() {
        updateState { it.copy(newPasswordVisible = !it.newPasswordVisible) }
    }

    fun onConfirmPasswordChange(password: String) {
        updateState {
            it.copy(
                confirmPassword = password,
                confirmPasswordIsError = false,
                confirmPasswordErrorMsg = ""
            )
        }
    }

    fun toggleConfirmPasswordVisibility() {
        updateState { it.copy(confirmPasswordVisible = !it.confirmPasswordVisible) }
    }

    fun onContinue() {
        if (validate()) {
            scope.launch {
                updateState { it.copy(isLoading = true) }

                commandBlock(showProgress = true) {
                    CommonCommands.changePasswordStaff(
                        ChangePasswordStaffRequestModel(newPassword = Security.hashPasswordForRequest(uiState.value.newPassword))
                    )
                        .onResponse(
                            success = { response ->
                                launchOnMain {
                                    updateState { it.copy(isLoading = false) }
                                    showSnackbar(sharedString(SR.strings.password_reset_success))
                                    setResult<LoginInterface> { goToMainScreen(false) }
                                }
                            },
                            baseError = { response ->
                                launchOnMain {
                                    updateState { it.copy(isLoading = false) }
                                    showSnackbar(Response.getErrorMessage(response.status, response.apiEndPoint, response.statusMessage))
                                }
                            }
                        )
                }
            }
        }
    }

    private fun validate(): Boolean {
        var isValid = true
        val state = uiState.value

        if (!state.passwordRequirements.isValid) {
            updateState {
                it.copy(
                    newPasswordIsError = true,
                    newPasswordErrorMsg = sharedString(SR.strings.password_requirements_not_met)
                )
            }
            isValid = false
        }

        if (state.newPassword != state.confirmPassword) {
            updateState {
                it.copy(
                    confirmPasswordIsError = true,
                    confirmPasswordErrorMsg = sharedString(SR.strings.passwords_do_not_match)
                )
            }
            isValid = false
        }

        return isValid
    }
}
